import Axios from 'axios'
import { omit, get } from 'lodash-es'
import { MODELS_API, getErrorMessage } from '@/main'
import { prepareChemicals, getChemicalUniqueKey } from '@/utils/chemical'
import {
  constructErrorResultsForEachChemical,
  enrichResponseWithSentStructures,
  constructValidationErrorResultsForEachChemical,
  defaultChemicalKey,
  defaultResultsKey,
  partitionChemicalsByValidation,
} from '@/utils/responseNormalizers'
import { modelStateRegistry } from '@/utils/modelStateRegistry'

const groupedModelState = get(modelStateRegistry, 'groupedModel')

const state = {
  ...groupedModelState.createState(),
}

const getters = {
  ...groupedModelState.createGetters(),
}

const mutations = {
  ...groupedModelState.createMutations(),
}

const keys = groupedModelState.keys
const getCurrentResultsForModel = (getters, modelId) => getters[keys.getResultsForModel]?.(modelId)

const actions = {
  async predict(context, { structures, modelId, batchSize = 200 } = {}) {
    if (!modelId) return
    const { commit, getters } = context
    commit(keys.RESET_STATE_FOR_MODEL, modelId)

    const { validChemicals, invalidChemicalsWithError } = partitionChemicalsByValidation(structures)
    const validationErrorResults = constructValidationErrorResultsForEachChemical({
      invalidChemicalsWithError,
      resultsKey: defaultResultsKey,
      chemicalKey: defaultChemicalKey,
    })

    commit(keys.PUSH_RESULTS_FOR_MODEL, { modelId, results: validationErrorResults })

    if (!validChemicals.length) {
      return getCurrentResultsForModel(getters, modelId)
    }

    const abortController = new AbortController()
    commit(keys.SET_ABORT_CONTROLLER_FOR_MODEL, { modelId, controller: abortController })
    commit(keys.SET_LOADING_FOR_MODEL, { modelId, loading: true })

    try {
      for (let i = 0; i < validChemicals.length; i += batchSize) {
        if (abortController.signal.aborted) break

        const batch = validChemicals.slice(i, i + batchSize)

        await this._vm.$axios
          .post(
            `${MODELS_API}/${modelId}`,
            { chemicals: prepareChemicals(batch, getChemicalUniqueKey) },
            { signal: abortController.signal },
          )
          .then((response) => {
            const enrichedResponse = enrichResponseWithSentStructures({
              responseData: response.data,
              sentStructures: structures,
              resultsKey: defaultResultsKey,
              chemicalKey: defaultChemicalKey,
              getSentChemicalIdentifier: getChemicalUniqueKey,
              getReceivedChemicalIdentifier: (chemical) => chemical?.id,
            })
            commit(keys.PUSH_RESULTS_FOR_MODEL, { modelId, results: enrichedResponse })
          })
          .catch((fetchError) => {
            if (fetchError.name === 'AbortError' || fetchError.name === 'CanceledError') return
            console.error('PFAS Atlas error: %o', getErrorMessage(fetchError))
            const networkErrorResults = constructErrorResultsForEachChemical({
              sentStructures: batch,
              errorMessage: getErrorMessage(fetchError),
              chemicalKey: defaultChemicalKey,
              resultsKey: defaultResultsKey,
            })
            commit(keys.PUSH_RESULTS_FOR_MODEL, { modelId, results: networkErrorResults })
          })
      }
    } finally {
      commit(keys.SET_ABORT_CONTROLLER_FOR_MODEL, { modelId, controller: null })
      commit(keys.SET_LOADING_FOR_MODEL, { modelId, loading: false })
    }

    return getCurrentResultsForModel(getters, modelId)
  },
}

function preparePfasAtlasMetadata(context) {
  const { commit } = context
  return Axios.get(`${MODELS_API}/pfas_atlas/metadata`)
    .then((response) => {
      commit(`pfasAtlas/${keys.SET_METADATA}`, response.data)
      commit('setFeatures', { pfasAtlas: { category: 'service' } })
    })
    .catch((error) => {
      console.warn('PFAS Atlas metadata is not available: %o', getErrorMessage(error))
      commit('removeFeature', { pfasAtlas: {} })
    })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

export { preparePfasAtlasMetadata }
